import { Button } from "@base/ui/Button/Button";
import { fetchPaymentStatus } from "@features/payment/api/request";
import type { PaymentMessage, PaymentStatus } from "@features/payment/model/types";
import { BaseLayout } from "@layouts/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/payment/success")({
  component: PaymentSuccessPage,
});

function isTerminal(status?: PaymentStatus) {
  return status === "COMPLETED" || status === "FAILED";
}

const TIMEOUT_MS = 25_000;

function PaymentSuccessPage() {
  const { t } = useTranslation();

  const { paymentId } = useSearch({
    strict: false,
    select: (params) => ({ paymentId: "payment_id" in params ? (params.payment_id as string) : undefined }),
  });

  const allowedOrigin = useMemo(() => window.location.origin, []);

  const startedAtRef = useRef<number>(Date.now());

  const { data: paymentStatus } = useQuery({
    queryKey: ["paymentStatus", paymentId],
    queryFn: () => fetchPaymentStatus(paymentId as string),
    enabled: !!paymentId,
    refetchInterval: (query) => {
      const data = query.state.data;
      const status = query.state.status;

      const elapsed = Date.now() - startedAtRef.current;

      if (elapsed > TIMEOUT_MS) return false;
      if (isTerminal(data?.status)) return false;
      if (status === "error") return false;

      return 800;
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: 2,
  });

  const postToOpener = useCallback(
    (message: PaymentMessage) => {
      // opener가 없을 수도 있으니 가드
      if (!window.opener || window.opener.closed) return;
      window.opener.postMessage(message, allowedOrigin);
    },
    [allowedOrigin],
  );

  const onConfirm = useCallback(() => {
    if (paymentStatus?.status === "COMPLETED") {
      postToOpener({
        type: "PAYMENT_SUCCEEDED",
        paymentId: paymentId as string,
      });
      window.close();
      return;
    }

    if (paymentStatus?.status === "FAILED") {
      postToOpener({
        type: "PAYMENT_FAILED",
        paymentId: paymentId as string,
      });
      window.close();
      return;
    }

    window.close();
  }, [paymentId, paymentStatus?.status, postToOpener]);

  return (
    <BaseLayout>
      <div className="flex justify-center items-center min-h-[calc(100vh-80px)] p-6">
        <div className="space-y-6 text-center">
          <p className="whitespace-pre-wrap text-body1">
            {paymentStatus?.status === "COMPLETED"
              ? t("apply.payment.successResultPageDescription")
              : paymentStatus?.status === "FAILED"
                ? t("apply.payment.failedResultPageDescription")
                : t("apply.payment.loadingResultPageDescription")}
          </p>
          {isTerminal(paymentStatus?.status) && (
            <Button variant="fill" color="primary" onClick={onConfirm}>
              {t("apply.payment.returnToPreviousPage")}
            </Button>
          )}
        </div>
      </div>
    </BaseLayout>
  );
}
